import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  disabled?: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Pagination component with page size dropdown and navigation controls
 */
export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  startIndex,
  endIndex,
  onPageChange,
  onPageSizeChange,
  disabled = false,
}: PaginationProps) {
  const canGoPrevious = page > 1;
  const canGoNext = page < totalPages;

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange(Number(event.target.value));
  };

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onPageChange(page + 1);
    }
  };

  // Generate page numbers to show (max 5 with ellipsis)
  const getPageNumbers = (): (number | "ellipsis")[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | "ellipsis")[] = [];

    if (page <= 3) {
      pages.push(1, 2, 3, 4, "ellipsis", totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "ellipsis", page - 1, page, page + 1, "ellipsis", totalPages);
    }

    return pages;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        px: 2,
        borderTop: 1,
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      {/* Page size selector */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Rows per page:
        </Typography>
        <FormControl size="small" disabled={disabled}>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ minWidth: 70 }}
            variant="outlined"
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Showing X to Y of Z */}
      <Typography variant="body2" color="text.secondary">
        {total === 0 ? "No documents" : `Showing ${startIndex} to ${endIndex} of ${total}`}
      </Typography>

      {/* Page navigation */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          onClick={handlePrevious}
          disabled={disabled || !canGoPrevious}
          size="small"
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </IconButton>

        {getPageNumbers().map((pageNum, index) =>
          pageNum === "ellipsis" ? (
            <Typography key={`ellipsis-${index}`} sx={{ px: 1 }} color="text.secondary">
              …
            </Typography>
          ) : (
            <IconButton
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={disabled}
              size="small"
              sx={{
                minWidth: 32,
                borderRadius: 1,
                backgroundColor: pageNum === page ? "primary.main" : "transparent",
                color: pageNum === page ? "primary.contrastText" : "text.primary",
                "&:hover": {
                  backgroundColor: pageNum === page ? "primary.dark" : "action.hover",
                },
              }}
            >
              {pageNum}
            </IconButton>
          )
        )}

        <IconButton
          onClick={handleNext}
          disabled={disabled || !canGoNext}
          size="small"
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
